import axios from './axiosConfig'
import { fromGatewayEnvelope } from '@/utils/gatewayError'

const CRM_TASKS_FAILED = 'טעינת משימות ה-CRM נכשלה'

// =============================================================================
// CRM tasks — /api/crm/{accountId}/...
// =============================================================================

export type CrmSection = 'open' | 'in_progress' | 'done'

export type APICrmStatusOption = {
  id: number
  key: string
  name: string
  isClosed: boolean
  section: CrmSection
}

export type APICrmTypeOption = {
  id: number
  name: string
}

export type APICrmAssigneeOption = {
  id: number
  name: string
}

export type APICrmPriorityOption = {
  value: number
  key: string
  name: string
}

export type APICrmReference = {
  statuses: APICrmStatusOption[]
  types: APICrmTypeOption[]
  assignees: APICrmAssigneeOption[]
  priorities: APICrmPriorityOption[]
}

export type APICrmTask = {
  id: number
  typeId: number
  typeName: string
  meterNumber: string
  address: string | null
  consumerName: string | null
  waterCard: string | null
  assigneeId: number
  assigneeName: string
  priority: number
  statusId: number
  statusKey: string
  statusName: string
  section: CrmSection
  description: string | null
  createdAt: string
  updatedAt: string
  dueAt: string | null
  overdue: boolean
  createdById: number | null
  createdByName: string | null
  updatedById: number | null
  updatedByName: string | null
}

export type APICrmComment = {
  activityId: number
  comment: string
  createdAt: string
  performedById: number | null
  performedByName: string | null
}

export type APICrmFile = {
  fileId: number
  name: string
  contentType: string | null
  byteSize: number | null
  uploadedAt: string
  uploadedById: number | null
  uploadedByName: string | null
}

export type APICrmTaskDetails = APICrmTask & {
  comments: APICrmComment[]
  files: APICrmFile[]
}

export type CrmTaskCreateRequest = {
  typeId: number
  /** Optional; a given meter must belong to the site. */
  meterNumber?: string
  assigneeId: number
  statusId?: number
  priority?: number
  dueAt?: string
  description?: string
  comment?: string
}

export type CrmTaskUpdateRequest = {
  typeId?: number
  /** A blank number removes the task's meter. */
  meterNumber?: string
  assigneeId?: number
  statusId?: number
  priority?: number
  dueAt?: string
  clearDueAt?: boolean
  description?: string
  comment?: string
}

export type CrmTaskCommentRequest = {
  comment: string
}

/** Filter names the tasks/list endpoint recognises. */
export type CrmFilterName =
  | 'section'
  | 'type'
  | 'assignee'
  | 'priority'
  | 'status'
  | 'meterNumber'
  | 'search'

export type CrmFilterResource = {
  name: CrmFilterName
  value: string[]
  operator?: string
}

export type CrmTaskFilter = {
  apiResources: CrmFilterResource[]
  logicalOperator?: 'AND' | 'OR'
}

export type CrmTaskSortBy = 'createdAt' | 'updatedAt' | 'dueAt' | 'priority'

export type CrmTaskListParams = {
  page?: number
  size?: number
  sortBy?: CrmTaskSortBy
  sortOrder?: 'ASC' | 'DESC'
  langKey?: string
}

export type APICrmTaskCollection = {
  total: number
  pageSize: number
  page: number
  result: APICrmTask[]
}

/**
 * Reference lists for the CRM screens (statuses, types, assignees, priorities).
 * GET /api/crm/{accountId}/reference
 */
export const getCrmReference = async (
  accountId: string | number,
  langKey = 'he'
): Promise<APICrmReference> => {
  const res = await axios.get(`/api/crm/${accountId}/reference`, {
    params: { langKey },
  })
  return res.data?.data
}

/**
 * Paged task list.
 * POST /api/crm/{accountId}/tasks/list
 *
 * Rejects when the site has no CRM schema, or when the answer carries no task
 * list, so callers can tell "no tasks" apart from "CRM is not installed here".
 */
export const listCrmTasks = async (
  accountId: string | number,
  filter: CrmTaskFilter,
  params: CrmTaskListParams = {},
  signal?: AbortSignal
): Promise<APICrmTaskCollection> => {
  const res = await axios.post(
    `/api/crm/${accountId}/tasks/list`,
    { logicalOperator: 'AND', ...filter },
    {
      params: {
        page: 1,
        size: 50,
        sortBy: 'updatedAt',
        sortOrder: 'DESC',
        langKey: 'he',
        ...params,
      },
      signal,
    }
  )
  const collection: APICrmTaskCollection | undefined = res.data?.data
  if (!Array.isArray(collection?.result)) {
    throw new Error(fromGatewayEnvelope(res.data, CRM_TASKS_FAILED).message)
  }
  return collection
}

/** GET /api/crm/{accountId}/tasks/{taskId} */
export const getCrmTask = async (
  accountId: string | number,
  taskId: number,
  langKey = 'he'
): Promise<APICrmTaskDetails> => {
  const res = await axios.get(`/api/crm/${accountId}/tasks/${taskId}`, {
    params: { langKey },
  })
  return res.data?.data
}

/** POST /api/crm/{accountId}/tasks */
export const createCrmTask = async (
  accountId: string | number,
  body: CrmTaskCreateRequest,
  langKey = 'he'
): Promise<APICrmTaskDetails> => {
  const res = await axios.post(`/api/crm/${accountId}/tasks`, body, {
    params: { langKey },
  })
  return res.data?.data
}

/** PATCH /api/crm/{accountId}/tasks/{taskId} — null/absent fields are unchanged. */
export const updateCrmTask = async (
  accountId: string | number,
  taskId: number,
  body: CrmTaskUpdateRequest,
  langKey = 'he'
): Promise<APICrmTaskDetails> => {
  const res = await axios.patch(`/api/crm/${accountId}/tasks/${taskId}`, body, {
    params: { langKey },
  })
  return res.data?.data
}

/** POST /api/crm/{accountId}/tasks/{taskId}/comments */
export const addCrmTaskComment = async (
  accountId: string | number,
  taskId: number,
  body: CrmTaskCommentRequest,
  langKey = 'he'
): Promise<APICrmTaskDetails> => {
  const res = await axios.post(
    `/api/crm/${accountId}/tasks/${taskId}/comments`,
    body,
    { params: { langKey } }
  )
  return res.data?.data
}

/** POST /api/crm/{accountId}/tasks/{taskId}/files (multipart, part `file`) */
export const uploadCrmTaskFile = async (
  accountId: string | number,
  taskId: number,
  file: File,
  langKey = 'he'
): Promise<APICrmTaskDetails> => {
  const form = new FormData()
  form.append('file', file)
  const res = await axios.post(
    `/api/crm/${accountId}/tasks/${taskId}/files`,
    form,
    { params: { langKey } }
  )
  return res.data?.data
}

/**
 * Downloads one attachment through the browser.
 * GET /api/crm/{accountId}/tasks/{taskId}/files/{fileId}
 */
export const downloadCrmTaskFile = async (
  accountId: string | number,
  taskId: number,
  file: Pick<APICrmFile, 'fileId' | 'name' | 'contentType'>
): Promise<void> => {
  const res = await axios.get(
    `/api/crm/${accountId}/tasks/${taskId}/files/${file.fileId}`,
    { responseType: 'blob' }
  )

  const disposition: string | undefined = res.headers['content-disposition']
  let filename = file.name
  if (disposition) {
    const encoded = disposition.match(/filename\*=UTF-8''([^;\n]+)/i)
    const plain = disposition.match(/filename="?([^";\n]+)"?/)
    if (encoded?.[1]) filename = decodeURIComponent(encoded[1])
    else if (plain?.[1]) filename = plain[1]
  }

  const blob = new Blob([res.data], {
    type:
      res.headers['content-type'] ||
      file.contentType ||
      'application/octet-stream',
  })
  const link = document.createElement('a')
  link.href = window.URL.createObjectURL(blob)
  link.download = filename
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  setTimeout(() => {
    document.body.removeChild(link)
    window.URL.revokeObjectURL(link.href)
  }, 1000)
}

/** DELETE /api/crm/{accountId}/tasks/{taskId} (soft delete) */
export const deleteCrmTask = async (
  accountId: string | number,
  taskId: number
): Promise<void> => {
  await axios.delete(`/api/crm/${accountId}/tasks/${taskId}`)
}

/** POST /api/crm/{accountId}/tasks/{taskId}/reminder — emails the assignee. */
export const sendCrmTaskReminder = async (
  accountId: string | number,
  taskId: number
): Promise<void> => {
  await axios.post(`/api/crm/${accountId}/tasks/${taskId}/reminder`)
}

/**
 * Pulls the gateway's error message out of an axios error. Blob responses
 * (file downloads) carry the JSON envelope as a Blob, so it is read as text.
 */
export const getCrmErrorMessage = async (
  error: unknown,
  fallback: string
): Promise<string> => {
  const data = (error as { response?: { data?: unknown } })?.response?.data
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text())
      return parsed?.message || parsed?.error || fallback
    } catch {
      return fallback
    }
  }
  const envelope = data as { message?: string; error?: string } | undefined
  return envelope?.message || envelope?.error || fallback
}
